import { useEffect, useState } from "react";
import { toast } from "sonner";
import { v4 } from "uuid";
import { Plus } from "lucide-react";
import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { hasInvalidRentingFeeRows } from "@/routes/base-data/processes/qpm/monitors/[monitorId]/_components/editable-renting-fees-table";
import type { MonitorAdditionalLibraryItemDTO } from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import {
  useQpmMonitorAdditionalsLibraryQuery,
  useQpmMonitorReplaceAdditionalLibraryMutation,
} from "@/services/backend/qpm/monitors/api/monitor-api";
import { MonitorType } from "@/services/backend/qpm/types/enums";
import { AdditionalOverviewTable } from "@/routes/base-data/processes/qpm/_components/additional-overview-table";
import { normalizeMonitorAdditionalConfig } from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import {
  MonitorAdditionalEditForm,
  rentingFeesToRows,
} from "@/routes/base-data/processes/qpm/_components/monitor-additional-edit-form";
import { MonitorAdditionalImageSlot } from "@/routes/base-data/processes/qpm/_components/monitor-additional-image-slot";

type LibraryAdditionalDraft = MonitorAdditionalLibraryItemDTO;
type DialogMode =
  | { mode: "create"; draft: LibraryAdditionalDraft }
  | { mode: "edit"; draft: LibraryAdditionalDraft };

const monitorTypes = [
  { value: MonitorType.Checkpoint, label: t("Prüfpunkte") },
  { value: MonitorType.FlyExterminator, label: t("Fluginsektenvernichter") },
  { value: MonitorType.IoT, label: t("IoT Monitore") },
];

export function MonitorAdditionalsLibraryRoutePage({
  monitorType,
}: {
  monitorType: MonitorType;
}) {
  const { data, isLoading, error } = useQpmMonitorAdditionalsLibraryQuery();
  const [replaceLibrary, replaceState] =
    useQpmMonitorReplaceAdditionalLibraryMutation();
  const [drafts, setDrafts] = useState<LibraryAdditionalDraft[]>([]);
  const [dialogMode, setDialogMode] = useState<DialogMode | null>(null);

  useEffect(() => {
    if (!data?.items) {
      return;
    }
    setDrafts(
      data.items.map((item) => ({
        ...item,
        id: item.id || v4(),
        description: item.description ?? "",
        basePrice: item.basePrice ?? 0,
        rentingFees: item.rentingFees ?? {},
        config: normalizeMonitorAdditionalConfig(item.config),
      })),
    );
  }, [data?.items]);

  const openCreateDialog = () => {
    const now = new Date().toISOString();
    setDialogMode({
      mode: "create",
      draft: {
        id: v4(),
        monitorType,
        name: "",
        description: "",
        basePrice: 0,
        rentingFees: {},
        config: normalizeMonitorAdditionalConfig(),
        createdAt: now,
        updatedAt: now,
        sourceMonitorId: "",
        sourceMonitorName: "",
      },
    });
  };

  const openEditDialog = (draft: LibraryAdditionalDraft) => {
    setDialogMode({ mode: "edit", draft });
  };

  const persistDrafts = async (nextDrafts: LibraryAdditionalDraft[]) => {
    await replaceLibrary({
      additionals: nextDrafts
        .filter((draft) => draft.name.trim() !== "")
        .map((draft) => ({
          id: draft.id,
          monitorType: draft.monitorType,
          name: draft.name,
          description: draft.description ?? "",
          basePrice: draft.basePrice ?? 0,
          rentingFees:
            draft.monitorType === MonitorType.IoT
              ? (draft.rentingFees ?? {})
              : {},
          config: normalizeMonitorAdditionalConfig(draft.config),
          createdAt: draft.createdAt,
          updatedAt: draft.updatedAt,
        })),
    }).unwrap();
  };

  const removeDraft = async (id: string) => {
    const nextDrafts = drafts.filter((draft) => draft.id !== id);
    try {
      await persistDrafts(nextDrafts);
      setDrafts(nextDrafts);
      toast.success(t("Material-Zusatz entfernt"));
    } catch {
      return;
    }
  };

  const isMutating = replaceState.isLoading;

  const rows = drafts
    .filter((draft) => draft.monitorType === monitorType)
    .map((draft) => ({
      id: draft.id,
      name: draft.name,
      monitorType: draft.monitorType,
      description: draft.description,
      basePrice: draft.basePrice ?? 0,
      updatedAt: draft.updatedAt,
      rentingFees: rentingFeesToRows(draft.rentingFees ?? {}),
      disabled: isMutating,
      onEdit: () => openEditDialog(draft),
      onDelete: () => removeDraft(draft.id),
    }));

  const updateDialogDraft = (patch: Partial<LibraryAdditionalDraft>) => {
    setDialogMode((current) => {
      if (!current) {
        return current;
      }
      const nextMonitorType = patch.monitorType ?? current.draft.monitorType;
      return {
        ...current,
        draft: {
          ...current.draft,
          ...patch,
          monitorType: nextMonitorType,
          updatedAt: new Date().toISOString(),
          rentingFees:
            nextMonitorType === MonitorType.IoT
              ? (patch.rentingFees ?? current.draft.rentingFees)
              : {},
        },
      };
    });
  };

  const confirmDialog = async () => {
    if (!dialogMode) {
      return;
    }
    const draft = {
      ...dialogMode.draft,
      name: dialogMode.draft.name.trim(),
      description: dialogMode.draft.description?.trim() ?? "",
    };
    if (!draft.name) {
      toast.error(t("Bitte vergeben Sie einen Namen."));
      return;
    }
    if (
      draft.monitorType === MonitorType.IoT &&
      hasInvalidRentingFeeRows(rentingFeesToRows(draft.rentingFees ?? {}))
    ) {
      toast.error(
        t(
          "Bitte prüfen Sie die Mietpreise. Laufzeiten dürfen nicht doppelt vorkommen.",
        ),
      );
      return;
    }
    const nextDrafts =
      dialogMode.mode === "create"
        ? [...drafts, draft]
        : drafts.map((currentDraft) =>
            currentDraft.id === draft.id ? draft : currentDraft,
          );
    try {
      await persistDrafts(nextDrafts);
      setDrafts(nextDrafts);
      setDialogMode(null);
      toast.success(t("Material-Zusatz gespeichert"));
    } catch {
      return;
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h3 className="text-lg font-semibold">{t("Material-Zusätze")}</h3>
          <p className="text-muted-foreground text-sm">
            {t(
              "Diese Vorlagen stehen beim Bearbeiten von Basis und Katalogmonitoren für den ausgewählten Monitor-Typ zur Auswahl.",
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={openCreateDialog}
            disabled={isLoading || isMutating}
          >
            <Plus className="mr-2 h-4 w-4" />
            {t("Material-Zusatz anlegen")}
          </Button>
        </div>
      </div>
      <div className="space-y-4">
        {error ? <RTKQueryErrorAlert error={error} /> : null}
        {replaceState.error ? (
          <RTKQueryErrorAlert error={replaceState.error} />
        ) : null}
        <AdditionalOverviewTable rows={rows} monitorType={monitorType} />
      </div>
      <AdditionalDialog
        mode={dialogMode}
        onChange={updateDialogDraft}
        onCancel={() => setDialogMode(null)}
        onConfirm={confirmDialog}
        isSaving={isMutating}
      />
    </section>
  );
}

function AdditionalDialog({
  mode,
  onChange,
  onCancel,
  onConfirm,
  isSaving,
}: {
  mode: DialogMode | null;
  onChange: (patch: Partial<LibraryAdditionalDraft>) => void;
  onCancel: () => void;
  onConfirm: () => void;
  isSaving: boolean;
}) {
  const draft = mode?.draft;

  return (
    <Dialog open={mode !== null} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-h-[92vh] w-[min(96vw,1280px)] max-w-none overflow-hidden p-0 sm:max-w-[1280px]">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle>
            {mode?.mode === "edit"
              ? t("Material-Zusatz bearbeiten")
              : t("Material-Zusatz anlegen")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "Legen Sie hier die Vorlage an, die später einem Monitor zugeordnet werden kann.",
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[calc(92vh-10rem)] overflow-y-auto px-6 py-4">
          {draft ? (
            <div className="grid gap-4">
              <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_180px]">
                <div className="space-y-2">
                  <Label>{t("Monitor-Typ")}</Label>
                  <Select
                    value={draft.monitorType}
                    onValueChange={(value) =>
                      onChange({ monitorType: value as MonitorType })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {monitorTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <MonitorAdditionalEditForm
                additional={draft}
                monitorType={draft.monitorType}
                disabled={isSaving}
                onChange={onChange}
                imageSlot={
                  <MonitorAdditionalImageSlot
                    additional={draft}
                    mode={{ kind: "library", additionalId: draft.id }}
                    disabled={isSaving}
                  />
                }
              />
            </div>
          ) : null}
        </div>
        <DialogFooter className="border-t px-6 py-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSaving}
          >
            {t("Abbrechen")}
          </Button>
          <Button type="button" onClick={onConfirm} disabled={isSaving}>
            {isSaving ? t("Speichern") : t("Speichern")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
